import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Select, TextArea, LoadingButton, useStateContext } from '@veda/dashboard-ui';
import {
  MdChevronLeft, MdCheckCircle, MdRadioButtonUnchecked, MdGavel, MdDescription,
  MdUploadFile, MdSend, MdAssignmentLate, MdOpenInNew,
} from 'react-icons/md';
import { usePropostaApi } from '../services/propostaApi';
import { usePppAiContext } from '../../ppp/hooks/usePppAiContext';

const TIPO_DOC_OPTIONS = [
  { value: 'pfte', label: 'PFTE' },
  { value: 'piano_economico_finanziario', label: 'PEF asseverato' },
  { value: 'schema_contratto', label: 'Bozza convenzione' },
  { value: 'relazione_tecnica', label: 'Relazione tecnica' },
  { value: 'studio_fattibilita', label: 'Studio di fattibilità' },
  { value: 'altro', label: 'Altro' },
];

const RICHIESTA_BADGE = {
  in_attesa:             { label: 'In attesa di risposta', cls: 'bg-amber-100 text-amber-700' },
  integrazione_ricevuta: { label: 'Integrazione inviata',  cls: 'bg-green-100 text-green-700' },
  respinta:              { label: 'Respinta',              cls: 'bg-red-100 text-red-700' },
};

// Mappa completa degli stati proposta (badge leggibili).
const STATO_BADGE = {
  bozza:                         { label: 'Bozza',                          cls: 'bg-gray-100 text-gray-600' },
  ricevuta:                      { label: 'Presentata',                     cls: 'bg-blue-100 text-blue-700' },
  in_valutazione:                { label: 'In valutazione',                 cls: 'bg-amber-100 text-amber-700' },
  interesse_pubblico_dichiarato: { label: 'Interesse pubblico dichiarato',  cls: 'bg-indigo-100 text-indigo-700' },
  ammessa:                       { label: 'Ammessa',                        cls: 'bg-green-100 text-green-700' },
  respinta:                      { label: 'Respinta',                       cls: 'bg-red-100 text-red-700' },
  ritirata:                      { label: 'Ritirata',                       cls: 'bg-gray-100 text-gray-500' },
};

const TIPO_DOC_LABEL = {
  pfte: 'PFTE', piano_economico_finanziario: 'PEF asseverato', schema_contratto: 'Bozza convenzione',
  relazione_tecnica: 'Relazione tecnica', studio_fattibilita: 'Studio di fattibilità',
};

function Badge({ stato }) {
  const b = STATO_BADGE[stato] ?? { label: stato, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${b.cls}`}>{b.label}</span>;
}

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Card di una richiesta di integrazione (Fase 10). Se "in_attesa" consente
// upload documenti + invio; altrimenti mostra stato/esito in sola lettura.
function IntegrazioneCard({ propostaId, richiesta, api, onChange, toast }) {
  const aperta = richiesta.stato === 'in_attesa';
  // La tendina riflette i tipi richiesti dall'ente (con il TipoDocumento da inviare).
  // Fallback alla lista completa se l'ente non ha specificato i tipi.
  const tipiOpzioni = (richiesta.tipiRichiesti?.length
    ? richiesta.tipiRichiesti.map((t) => ({ value: t.tipoDocumento, label: t.label }))
    : TIPO_DOC_OPTIONS);
  const [tipo, setTipo] = useState(tipiOpzioni[0]?.value ?? 'altro');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [inviando, setInviando] = useState(false);
  const fileRef = React.useRef(null);
  const b = RICHIESTA_BADGE[richiesta.stato] ?? { label: richiesta.stato, cls: 'bg-gray-100 text-gray-600' };

  const carica = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tipo', tipo);
    api.uploadDocumentoIntegrazione(propostaId, richiesta.id, fd, {
      onSuccess: () => { setBusy(false); if (fileRef.current) fileRef.current.value = ''; toast('Documento caricato.'); onChange(); },
      onError:   () => { setBusy(false); toast('Errore upload.', 'error'); },
      onFormError: () => { setBusy(false); toast('Errore upload.', 'error'); },
    });
  };

  const invia = () => {
    if (!richiesta.documenti?.length) { toast('Carica almeno un documento prima di inviare.', 'error'); return; }
    setInviando(true);
    api.rispondiIntegrazione(propostaId, richiesta.id, { note: note || null }, {
      onSuccess: () => { setInviando(false); toast('Integrazioni inviate all\'ente.'); onChange(); },
      onFormError: (err) => { setInviando(false); toast(err?.message ?? 'Errore nell\'invio.', 'error'); },
    });
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">{richiesta.descrizione || 'Richiesta di integrazione'}</p>
          {richiesta.termineRisposta && (
            <p className="text-xs text-gray-500 mt-0.5">Termine per la risposta: <strong>{formatDate(richiesta.termineRisposta)}</strong></p>
          )}
          {richiesta.tipiRichiesti?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              <span className="text-xs text-gray-500 mr-1">Documenti richiesti:</span>
              {richiesta.tipiRichiesti.map((t) => (
                <span key={t.value} className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-xs rounded">{t.label}</span>
              ))}
            </div>
          )}
        </div>
        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${b.cls}`}>{b.label}</span>
      </div>

      {/* Documenti già caricati */}
      {richiesta.documenti?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {richiesta.documenti.map((d) => (
            <li key={d.id} className="flex items-center gap-2 text-sm bg-gray-50 dark:bg-gray-700/40 rounded px-3 py-1.5">
              <MdCheckCircle className="text-green-500" /> <span className="text-gray-700 dark:text-gray-200">{d.nome}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Esito valutazione (Fase 11) */}
      {richiesta.esitoLabel && (
        <div className="mt-3 text-sm">
          <span className="text-gray-500">Esito valutazione: </span>
          <strong className="text-gray-800 dark:text-gray-100">{richiesta.esitoLabel}</strong>
          {richiesta.comunicazione && <p className="text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-line">{richiesta.comunicazione}</p>}
        </div>
      )}
      {richiesta.noteRespingimento && (
        <p className="mt-2 text-sm text-red-600 whitespace-pre-line">{richiesta.noteRespingimento}</p>
      )}

      {/* Azioni di risposta (solo se aperta) */}
      {aperta && (
        <div className="mt-4 border-t border-gray-100 dark:border-gray-700 pt-3 space-y-3">
          <div className="flex items-end gap-2">
            <div className="w-56">
              <Select label="Tipo documento" value={tipo} onChange={(e) => setTipo(e?.target ? e.target.value : e)} options={tipiOpzioni} />
            </div>
            <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm font-medium cursor-pointer">
              <MdUploadFile /> {busy ? 'Caricamento…' : 'Carica documento'}
              <input ref={fileRef} type="file" className="hidden" onChange={carica} disabled={busy} />
            </label>
          </div>
          <TextArea label="Nota di accompagnamento (facoltativa)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex justify-end">
            <LoadingButton isLoading={inviando} customFunc={invia} text="Invia integrazioni" icon={<MdSend />} />
          </div>
        </div>
      )}
    </div>
  );
}

function DownloadAttoButton({ propostaId, nomeFile, api }) {
  const [busy, setBusy] = useState(false);

  const handleClick = () => {
    setBusy(true);
    api.downloadAttoProvvedimento(propostaId, nomeFile, {
      onSuccess: () => setBusy(false),
      onError:   () => setBusy(false),
      onFormError: () => setBusy(false),
    });
  };

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-wait"
    >
      <MdOpenInNew className="shrink-0" />
      {busy ? 'Download…' : `Scarica atto: ${nomeFile}`}
    </button>
  );
}

export default function PropostaAvanzamentoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  usePppAiContext('ppp.proposte', { id });
  const api = usePropostaApi();
  const { addToast } = useStateContext();
  const toast = (testo, tipo = 'success') => addToast?.({ type: tipo, title: testo });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const carica = useCallback(() => {
    api.avanzamento(id, {
      onSuccess: (d) => { setData(d); setLoading(false); },
      onFormError: () => { navigate('/operatore/proposte'); },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  if (loading) return <div className="flex items-center justify-center min-h-64"><Spinner /></div>;
  if (!data) return null;

  const a = data.avanzamento;
  const cronologia = data.cronologia ?? [];

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <button onClick={() => navigate('/operatore/proposte')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
        <MdChevronLeft /> Le mie proposte
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{a.titolo}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {a.procedimentoTitolo}{a.enteConcedente ? ` — ${a.enteConcedente}` : ''}
            </p>
            {a.numeroProtocollo && (
              <p className="text-sm text-gray-500 mt-1">Protocollo: <strong>{a.numeroProtocollo}</strong>
                {a.dataPresentazione && ` · Presentata il ${formatDate(a.dataPresentazione)}`}</p>
            )}
          </div>
          <Badge stato={a.stato} />
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-4">Avanzamento</h2>
        <ol className="space-y-4">
          {cronologia.map((ev) => (
            <li key={ev.evento} className="flex items-start gap-3">
              {ev.fatto
                ? <MdCheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={20} />
                : <MdRadioButtonUnchecked className="text-gray-300 mt-0.5 flex-shrink-0" size={20} />}
              <div>
                <p className={`text-sm ${ev.fatto ? 'text-gray-800 dark:text-gray-100 font-medium' : 'text-gray-400'}`}>
                  {ev.descrizione}
                </p>
                {ev.data && <p className="text-xs text-gray-400">{formatDate(ev.data)}</p>}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Esito valutazione pubblica */}
      {(a.esitoValutazione || a.noteValutazione) && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
            <MdGavel /> Valutazione dell'ente
          </h2>
          {a.esitoValutazione && <p className="text-sm text-gray-700 dark:text-gray-200">Esito: <strong>{a.esitoValutazione}</strong></p>}
          {a.noteValutazione && <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 whitespace-pre-line">{a.noteValutazione}</p>}
        </div>
      )}

      {/* Interesse pubblico */}
      {a.interessePubblicoDichiarato && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6 border border-indigo-100 dark:border-indigo-800">
          <h2 className="text-base font-semibold text-indigo-700 dark:text-indigo-300 mb-1">Interesse pubblico dichiarato</h2>
          {a.dataPubblicazioneInteressePubblico && (
            <p className="text-sm text-indigo-600 dark:text-indigo-400">
              Pubblicata il {formatDate(a.dataPubblicazioneInteressePubblico)}
              {a.termineProposteConcorrenti && ` · Termine proposte concorrenti: ${formatDate(a.termineProposteConcorrenti)}`}
            </p>
          )}
        </div>
      )}

      {/* Provvedimento finale */}
      {a.provvedimento && (
        <div className={`rounded-xl p-6 border ${a.provvedimento.esito === 'ammessa'
          ? 'bg-green-50 border-green-100 dark:bg-green-900/20 dark:border-green-800'
          : 'bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-800'}`}>
          <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-1">
            Provvedimento finale: proposta {a.provvedimento.esito}
          </h2>
          {a.provvedimento.motivazione && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-line">{a.provvedimento.motivazione}</p>}
          {a.provvedimento.pubblicatoIl && <p className="text-xs text-gray-500 mt-2">Pubblicato il {formatDate(a.provvedimento.pubblicatoIl)}</p>}
          {a.provvedimento.documentoAtto && (
            <DownloadAttoButton
              propostaId={id}
              nomeFile={a.provvedimento.documentoAtto.nome}
              api={api}
            />
          )}
        </div>
      )}

      {/* Richieste di integrazione (Fase 10) */}
      {a.integrazioni?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
            <MdAssignmentLate /> Richieste di integrazione documentale
          </h2>
          <div className="space-y-4">
            {a.integrazioni.map((r) => (
              <IntegrazioneCard
                key={r.id}
                propostaId={id}
                richiesta={r}
                api={api}
                onChange={carica}
                toast={toast}
              />
            ))}
          </div>
        </div>
      )}

      {/* Documenti presentati */}
      {a.documenti?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
            <MdDescription /> Documenti presentati
          </h2>
          <ul className="space-y-1.5">
            {a.documenti.map((d) => (
              <li key={d.id} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700/40 rounded px-3 py-1.5">
                <span className="text-gray-700 dark:text-gray-200">{d.nome}</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{TIPO_DOC_LABEL[d.tipo] ?? d.tipo}</span>
                  {d.contieneDatiRiservati && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">riservato</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
